import { css } from '@emotion/react'

import { Link as RouterLink } from 'react-router-dom'
import styled from '@emotion/styled'
import { keyframes } from '@emotion/react'
// @reach/dialog no es compatible con React 18, usar Modal de Chakra UI en su lugar
// import { Dialog as ReachDialog } from '@reach/dialog'
import { FaSpinner } from 'react-icons/fa'
import * as colors from '../styles/colors'
import * as mq from '../styles/media-queries'


const WrapperMain = styled.main({
    minHeight: 'calc(100vh - 60px)',
    position: 'inherit',
    margin: '60px 0 0 280px',
    background: colors.bgApp,
    [mq.small]: {
        margin: '60px 0 0 0'
    }
});

const Container = styled.div`
    padding-left: 25px;
    padding-right: 25px;
    padding-bottom: 15px;
    padding-top: 10px;

    @media (max-width: 600px) {
        padding-left: 4px;
        padding-right: 4px;
        padding-bottom: 8px;
        padding-top: 4px;
    }
`

const spin = keyframes({
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
})

const CircleButton = styled.button({
    borderRadius: '30px',
    padding: '0',
    width: '40px',
    height: '40px',
    lineHeight: '1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: colors.base,
    color: colors.text,
    border: `1px solid ${colors.gray10}`,
    cursor: 'pointer',
})

const ButtonIcon = styled.div`
    background: transparent;
    cursor: pointer;
`;

const TableResponsive = styled.div`
    overflow-x: auto;
    overflow-y: hidden;
    width: 100%;
    table {
        background: white;
        width: 100%;
        max-width: 100%;
        margin-bottom: 10px;
        border-spacing: 0;
        border-collapse: collapse;
        border-bottom: 1px solid ${colors.gray80};
        thead > tr > th {
            vertical-align: bottom;
            background-color: ${colors.gray20};
            border-bottom: 1px solid #e4e7ea;
            padding: 15px 8px;
            font-weight: 500;
            text-align: left;
        }
        tbody > tr > td {
            border-top: 1px solid #e4e7ea;
            padding: 11px 8px;
            vertical-align: top;
            text-align: left;
            font-size: 14px;
            color: ${colors.gray80}
        }
    }

    /* Debajo de 600px la tabla se convierte en una lista de tarjetas - cada
       fila pasa a ser una tarjeta y cada celda muestra su encabezado como
       etiqueta (via data-label, seteado en Table.js), en vez de exigir
       scroll horizontal para leer una tabla angosta de mas columnas de las
       que caben en un telefono. */
    @media (max-width: 600px) {
        overflow-x: visible;
        table, thead, tbody, tr, td {
            display: block;
            width: 100%;
        }
        table {
            border-bottom: none;
        }
        thead {
            display: none;
        }
        tbody > tr {
            border: 1px solid #e4e7ea;
            border-radius: 14px;
            margin-bottom: 10px;
            padding: 6px 10px;
            background: white;
        }
        tbody > tr > td {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            gap: 12px;
            border-top: none;
            border-bottom: 1px dashed #e4e7ea;
            padding: 9px 0;
            text-align: right;
        }
        tbody > tr > td:last-child {
            border-bottom: none;
        }
        tbody > tr > td[data-label]::before {
            content: attr(data-label);
            flex-shrink: 0;
            font-weight: 600;
            font-size: 12px;
            color: ${colors.textMain};
            text-align: left;
        }
        tbody > tr > td:not([data-label])::before,
        tbody > tr > td[data-label=""]::before {
            content: none;
        }
    }
`

const Spinner = styled(FaSpinner)({
    animation: `${spin} 1s linear infinite`,
})

Spinner.defaultProps = {
    'aria-label': 'loading',
}

const buttonVariants = {
    primary: {
        background: colors.buttonGradient,
        color: colors.white,
        boxShadow: '0 10px 24px rgba(88, 62, 255, 0.28)',
    },
    gradient: {
        background: colors.buttonGradient,
        color: colors.white,
        boxShadow: '0 10px 24px rgba(88, 62, 255, 0.28)',
    },
    primaryDark: {
        background: colors.bluedark,
        color: colors.base,
    },
    black: {
        background: colors.black,
        color: colors.base,
    },
    secondary: {
        background: colors.blue,
        color: colors.base,
    },
    default: {
        background: colors.white,
        color: colors.textMain,
        border: `1px solid ${colors.borderMid}`,
    },
    ghost: {
        background: colors.white,
        color: '#4f4672',
        border: `1px solid ${colors.borderMid}`,
    },
    outline: {
        background: colors.white,
        color: '#6b57a8',
        border: '1px solid #ddd1fa',
    },
    danger: {
        background: colors.danger,
        color: colors.base,
    },
}

const buttonSize = {
    xs: {
        fontSize: '0.9rem',
        padding: '5px 8px', 
    },
    sm: {
        fontSize: '0.9rem',
        padding: '8px 10px', 
    },
    md: {
        fontSize: '1rem',
        padding: '10px 15px', 
    },
    lg: {
        fontSize: '1.15rem',
        padding: '10px 18px', 
    }
}

const Button = styled.button(
    {
        position: 'relative',
        overflow: 'hidden',
        padding: '10px 20px',
        border: '0',
        lineHeight: '1',
        borderRadius: '16px',
        cursor: 'pointer',
        fontWeight: 600,
        transition: '0.2s ease',
        '&:hover': {
            transform: 'translateY(-1px)'
        }
    },
    ({m = 0}) => css`margin:${m}`,
    ({ variant = 'default' }) => buttonVariants[variant],
    ({ size = 'md' }) => buttonSize[size],
    ({disabled}) => disabled && css`::after {
                                        content: ' ';
                                        width: 100%;
                                        left: 0;
                                        top: 0;
                                        bottom: 0;
                                        right: 0;
                                        position: absolute;
                                        background: #ffffff40;
                                    }`,
    ({isLoading}) => isLoading && css`
    padding-left: 33px;
    &::before {
        content: ' ';
        display: block;
        border: 3px solid rgba(255, 255, 255, 0.5);
        border-radius: 50%;
        border-top-color: white;
        opacity: 1;
        position: absolute;
        left: 5%;
        top: 25%;
        bottom: 25%;
        margin: auto;
        width: 16px;
        height: 16px;
        transition: opacity 200ms;
        animation: ${spin} 1s linear infinite;
        transition-delay: 200ms
    }
  `
)

const inputStyles = {
    border: '1px solid #dcd6e8',
    background: colors.white,
    padding: '12px 14px',
    width: '100%',
    boxSizing: 'border-box',
    transition: '.2s all ease-out',
    fontSize: '14px',
    color: colors.textMain,
    '&:focus': {
        borderColor: '#bbaaf6',
        boxShadow: '0 0 0 4px rgba(130, 90, 255, 0.08)',
        outline: 'none'
    }
}

const Input = styled.input({ borderRadius: '14px', minHeight: '44px' }, inputStyles)
const Textarea = styled.textarea({ borderRadius: '14px', minHeight: '110px' }, inputStyles)
const Select = styled.select({ borderRadius: '14px', minHeight: '44px' }, inputStyles)
const Label = styled.label({
    width: '100%',
    display: 'block',
    fontSize: '.9rem',
    color: colors.gray80,
    marginBottom: '4px'
});


const InputFilter = styled.input({
    border: '1px solid #f1f1f4',
    background: '#fff',
    padding: '8px 12px',
    width: '100%',
    boxSizing: 'border-box',
    borderRadius: '3px',
    transition: '.5s all ease-out',
    fontSize: '15px'
});

const TitleFilter = styled.div`
    width: 100%;
    color: ${colors.gray90};
    margin-bottom: 5px;
    text-transform: uppercase;
    font-size: 1rem;
`

// TODO: Reemplazar con Modal de Chakra UI
// const Dialog = styled(ReachDialog)({
//     maxWidth: '450px',
//     borderRadius: '3px',
//     paddingBottom: '3.5em',
//     boxShadow: '0 10px 30px -5px rgba(0, 0, 0, 0.2)',
//     margin: '20vh auto',
//     [mq.small]: {
//         width: '100%',
//         margin: '10vh auto',
//     },
// })

const FormGroup = styled.div({
    display: 'flex',
    flexDirection: 'column',
})

/** NAVBAR **/
const Navbar = styled.nav`
    position: fixed;
    right: 0;
    top: 0;
    width: calc(100% - 240px);
    z-index: 1010;
    .navbar-header {
        width: 100%;
        padding: 5px;
        height: 60px;
        background: ${colors.primary};
        display: flex;
        justify-content: space-between;
        ul {
            li {
                padding: 3px 5px;
            }
        }
        button.toggle-sidebar {
            background: none;
            border: none;
            cursor: pointer;
            color: white;
            :focus {
                outline: none;
            }
        }
    }
    ${mq.small} {
        left: 0;
        width: 100%;
    }
`

/** NAVBAR DROPDOWN  **/
const DropDownMenu = styled.ul`

  /* main UL component called: "Menu" */
  list-style-type: none;
  margin: 0;
  padding: 0;
  overflow: hidden;
  background-color: transparent;

  /* main LI */
  & > li {
    float: left;

    & > a, > button {
      position: relative;
      display: inline-block;
      color: ${colors.base};
      text-align: center;
      padding: 14px 20px 14px 16px;
      text-decoration: none;
      background-color: transparent;
      border: none;
      cursor: pointer;
      font-size: 15px;
      &::after {
            content: '';
            position: absolute;
            right: 0px;
            top: 50%;
            transform: translateY(-50%); 
            width: 0; 
            height: 0; 
            border-left: 6px solid transparent;
            border-right: 6px solid transparent;
            border-top: 6px solid #fff;
            transition: all .3s ease-out;
      }
      &:focus {
        outline: none;
      }
    }
  }

  /* dropdown LI */
  & > .dropdown {
      display: inline-block;
      padding: 5px;
      & > .dropdown-content {
        display: none;
        position: absolute;
        top: 100%;
        right: 10px;
        background-color: ${colors.base};
        min-width: 110px;
        border: 1px solid #cacaca;
        z-index: 1;

        & > a, button {
          color: black;
          padding: 12px 16px;
          text-decoration: none;
          display: block;
          text-align: left;
          border: none;
          width: 100%;
          font-size: 14px;
          background-color: ${colors.base};
          &:hover {
            background-color: ${colors.gray20};
            cursor: pointer;
          }
        }
      }

      &.opened { 
        & > button {
            &::after {
                content: '';
                position: absolute;
                right: 0px;
                top: 50%;
                transform: translateY(-50%) rotate(180deg);
            }
        }
        .dropdown-content {
            display: block
        }
      }
    }
`

/** SIDEBAR **/
const Sidebar = styled.div`
    background: #fff;
    border-right: 1px solid ${colors.gray20};
    position: fixed;
    width: 240px;
    padding-top: 0;
    top: 0;
    height: 100%;
    transition: .6s ease-out;
    z-index: 900;
    left: 0;
    transform: ${props => props.visible ? 'translateX(0)' : 'translateX(-240px)'};
    overflow-y: scroll;
    overflow-x: hidden;
`

const SidebarHead = styled.div`
    position: absolute;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 240px;
    height: 60px;
    z-index: 10;
    text-transform: uppercase;
    border-bottom: 1px solid ${colors.gray20}
`

const SidebarListItems = styled.ul`
    list-style: none;
    padding: 0;
    overflow: hidden;
    margin-top: 60px;
    li {
        position: relative;
        display: block;
        a {
            position: relative;
            padding: 15px 35px 15px 20px;
            display: block;
            font-size: 14px;
            overflow: hidden;
            transition: .05s ease-in;
            .icon {
                width: 38px;
                display: inline-block;
                text-align: center!important;
                font-style: normal;
                font-weight: 500;
                margin-right: 7px;
                font-size: 16px;
                vertical-align: middle;
            }
            &:hover {
                background: rgba(0, 0, 0, 0.07);
                text-decoration: none;
            }
        }
        div.item-collapse {
            button {
                background: transparent;
                width: 100%;
                border: none;
                padding: 15px 35px 15px 20px;
                display: block;
                font-size: 14px;
                overflow: hidden;
                text-align: left;
                cursor: pointer;
                transition: .05s ease-in;
                img.icon {
                    width: 38px;
                    display: inline-block;
                    text-align: center!important;
                    font-style: normal;
                    font-weight: 500;
                    margin-right: 7px;
                    font-size: 16px;
                    vertical-align: middle;
                }
                &:hover {
                    background: rgba(0, 0, 0, 0.07);
                    text-decoration: none;
                }
                &:focus {
                    outline: none;
                }
            }
            ul {
                background-color: ${colors.gray20}
            }
        }
    }
`

const ErrorText = styled.div`
    color: ${colors.danger};
    font-size: .9rem;
    margin-top: 7px;
`


const linkVariants = {
    primary: {
        color: colors.indigo
    },
    black: {
        color: colors.black
    },
    danger: {
        color: colors.danger
    }
};

const Link = styled.a`
    &:hover,
    &:focus {
        text-decoration: underline;
        cursor: pointer;
        outline: none;
    }
    ${({ variant = 'primary' }) => linkVariants[variant]}
    ${({m = 0}) => `margin:${m}`}
`
const LinkButton = styled.button`
    background-color: transparent;
    border: none;
    font-size: 16px;
    &:hover,
    &:focus {
        text-decoration: underline;
        cursor: pointer;
        outline: none;
    }
    ${({ variant = 'primary' }) => linkVariants[variant]}
    ${({m = 0}) => `margin:${m}`}
`

const LinkItem = styled.button`
    background-color: transparent;
    border: none;
    font-size: 13px;
    cursor: pointer;
`;

const MenuList = styled.div`
    border: 1px solid #cacaca;
`;

const StyleInfoPatient = {
    color: colors.gray80,
    fontSize: '0.95rem',
    paddingTop: '6px',
    lineHeight: '20px'
}

const StyleDiagnosticState = {
    activo: {
        color: colors.base,
        backgroundColor: colors.blue,
        borderRadius: '5px',
        padding: '0 4px'
    },
    confirmado: {
        color: colors.base,
        backgroundColor: colors.bluedark,
        borderRadius: '5px',
        padding: '0 4px'
    },
    hipotesis_diagnostica: {
        color: colors.base,
        backgroundColor: colors.gray80,
        borderRadius: '5px',
        padding: '0 4px'
    },
    recuperado: {
        color: colors.base,
        backgroundColor: colors.yellow,
        borderRadius: '5px',
        padding: '0 4px'
    },
    dado_alta: {
        color: colors.base,
        backgroundColor: colors.green,
        borderRadius: '5px',
        padding: '0 4px'
    },
    descartado: {
        color: colors.base,
        backgroundColor: colors.danger,
        borderRadius: '5px',
        padding: '0 4px'
    }
}

const errorMessageVariants = {
    stacked: { display: 'block' },
    inline: { display: 'inline-block' },
}

const CalendarContainer = styled.div`
  /* Variables de FullCalendar afinadas al tema de la app (morado/suave). */
  .fc {
    font-family: inherit;
    font-size: 14px;
    --fc-border-color: #ece7f5;
    --fc-today-bg-color: rgba(125, 35, 255, 0.045); /* reemplaza el amarillo por defecto de "hoy" */
    --fc-now-indicator-color: #e53e3e;
    --fc-page-bg-color: #ffffff;
    --fc-neutral-bg-color: #faf9fd;
  }

  /* Marco redondeado, integrado con las tarjetas del sistema. */
  .fc-scrollgrid {
    border-radius: 16px;
    overflow: hidden;
  }
  .fc-scrollgrid,
  .fc-scrollgrid td:last-of-type {
    border-right: none;
  }

  .fc-toolbar.fc-header-toolbar {
    margin-bottom: 18px;
    flex-wrap: wrap;
    gap: 8px;
  }
  .fc-toolbar-title {
    font-size: 1.15rem;
    font-weight: 700;
    color: ${colors.textMain};
    text-transform: capitalize;
  }

  /* Botones: redondeados, morado de marca, con estado activo claro. */
  .fc .fc-button {
    background-color: #f1ecff;
    border: none;
    color: ${colors.primary};
    font-weight: 600;
    text-transform: capitalize;
    border-radius: 10px;
    padding: 6px 14px;
    box-shadow: none;
    transition: background-color 0.15s ease;
  }
  .fc .fc-button:hover {
    background-color: #e6ddff;
    color: ${colors.primary};
  }
  .fc .fc-button:focus,
  .fc .fc-button:focus-visible {
    box-shadow: none;
    outline: none;
  }
  .fc .fc-button-primary:disabled {
    background-color: #f4f2fa;
    color: #b9b2cc;
  }
  .fc .fc-button-primary:not(:disabled).fc-button-active,
  .fc .fc-button-primary:not(:disabled):active {
    background-color: ${colors.primary};
    color: #ffffff;
  }
  .fc .fc-button-group > .fc-button:not(:last-child) {
    margin-right: 4px;
  }

  /* Encabezado de días (dom, lun, ...). */
  .fc-col-header-cell {
    background: #faf9fd;
    padding: 8px 0;
  }
  .fc-col-header-cell-cushion {
    color: ${colors.textSoft};
    font-weight: 600;
    font-size: 12px;
    text-transform: capitalize;
    text-decoration: none;
  }
  .fc-col-header-cell.fc-day-today .fc-col-header-cell-cushion {
    color: ${colors.primary};
  }

  /* Etiquetas de hora y número de día. */
  .fc-timegrid-slot-label-cushion,
  .fc-timegrid-axis-cushion {
    color: ${colors.textSoft};
    font-size: 12px;
  }
  .fc-daygrid-day-number {
    font-weight: 600;
    color: ${colors.textMain};
    text-decoration: none;
  }

  /* Eventos. */
  .fc-event-consulta {
    background-color: #3182ce;
    border: none;
    color: white;
    padding: 2px 6px;
    border-radius: 6px;
  }
  .fc-cita-primera {
    background-color: #7d23ff; /* morado - Primera cita */
    border: none;
    color: white;
    padding: 1px 4px;
    border-radius: 6px;
    cursor: pointer;
  }
  .fc-cita-control {
    background-color: #2f855a; /* verde - Control */
    border: none;
    color: white;
    padding: 1px 4px;
    border-radius: 6px;
    cursor: pointer;
  }
  .fc-cita-past {
    opacity: 0.5;
  }
  .fc-event.fc-cita-past {
    cursor: default;
  }
  .fc-timegrid-event,
  .fc-daygrid-event {
    box-shadow: none;
  }
  .fc-v-event {
    border: none;
  }

  .fc-non-business {
    background: rgba(125, 35, 255, 0.045);
  }
`;

function ErrorMessage({ error, variant = 'stacked', ...props }) {
    return (
        <div role="alert" css={[{ color: colors.danger }, errorMessageVariants[variant]]} {...props}>
            <span>There was an error: </span>
            <pre css={[errorMessageVariants[variant]]}>
                {error.message}
            </pre>
        </div>
    )
}

function FullPageErrorFallback({ error }) {
    return (
        <div style={{ color: colors.danger, height: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center'}}>
            <p>Hubo un error. Intente actualizar la página.</p>
            <pre>{error.message}</pre>
        </div>
    )
}

const customStylesSelect = {
    option: (provided, state) => ({
      ...provided,
      borderBottom: '1px dotted pink',
      color: 'black',
      padding: 20,
    }),
    // control: () => ({
    //   // none of react-select's styles are passed to <Control />
    //   width: 200,
    // }),
    singleValue: (provided, state) => {
      const opacity = state.isDisabled ? 0.5 : 1;
      const transition = 'opacity 300ms';
  
      return { ...provided, opacity, transition };
    }
  }



export {
    FullPageErrorFallback,
    ErrorMessage,
    ErrorText,
    WrapperMain,
    Container,
    CircleButton,
    TableResponsive,
    Spinner,
    Button,
    ButtonIcon,
    InputFilter,
    TitleFilter,
    Input,
    Select,
    Textarea,
    Navbar,
    Sidebar,
    SidebarHead,
    SidebarListItems,
    Label,
    // Dialog, // Comentado - usar Modal de Chakra UI
    FormGroup,
    DropDownMenu,
    Link,
    LinkButton,
    LinkItem,
    MenuList,
    StyleInfoPatient,
    StyleDiagnosticState,
    customStylesSelect,
    CalendarContainer
}